// ---------------------------------------------------------------------------
// C88-C93: Community Hooks — spaces, posts, comments, reactions, gamification
// ---------------------------------------------------------------------------
import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CommunitySpace {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  coverImage: string | null;
  accessType: "free" | "paid" | "tiered";
  memberCount: number;
  postCount: number;
  isArchived: boolean;
  isMember: boolean;
  membershipRole?: "member" | "moderator" | "admin" | null;
  settings?: {
    aiManager: {
      enabled: boolean;
      autoModerate: boolean;
      autoReplyToQuestions: boolean;
    };
  };
}

export interface CommunityMember {
  userId: string;
  userName: string;
  avatarUrl?: string | null;
  role: "member" | "moderator" | "admin";
  level: number;
  points: number;
  joinedAt: string;
  postCount: number;
  spaceCount?: number;
  lastActiveAt?: string | null;
  badges?: Array<{ id: string; name: string; iconUrl: string | null }>;
}

export interface CommunityPost {
  id: string;
  spaceId: string;
  authorId: string;
  authorName: string;
  postType: "post" | "poll" | "question" | "milestone" | "announcement";
  title: string | null;
  content: string;
  mediaUrls: string[];
  pollOptions: Array<{ id: string; text: string; votes: number }> | null;
  isPinned: boolean;
  isLocked: boolean;
  commentCount: number;
  reactionCount: number;
  myReaction: string | null;
  createdAt: string;
}

export interface CommunityComment {
  id: string;
  postId: string;
  parentId: string | null;
  authorId: string;
  authorName: string;
  content: string;
  likesCount: number;
  createdAt: string;
  replies: CommunityComment[];
}

export interface LeaderboardEntry {
  userId: string;
  userName: string;
  level: number;
  points: number;
  badgeCount: number;
  rank: number;
}

export interface CommunityBadgeAwardCriteria {
  type: "manual" | "post_count" | "comment_count" | "reaction_count" | "level_reached" | "points_threshold";
  threshold: number;
  metric: string;
}

export interface CommunityBadge {
  id: string;
  tenantId: string;
  spaceId: string | null;
  name: string;
  description: string | null;
  iconUrl: string | null;
  awardCriteria: CommunityBadgeAwardCriteria;
  pointsValue: number;
  isActive: boolean;
  createdAt: string;
}

export interface CreateCommunityBadgeInput {
  name: string;
  description?: string;
  iconUrl?: string;
  awardCriteria: CommunityBadgeAwardCriteria;
  pointsValue?: number;
}

export interface CommunityBadgeAward {
  id: string;
  tenantId: string;
  badgeId: string;
  userId: string;
  awardedAt: string;
}

export interface CommunityAnalyticsSummary {
  totalMembers: number;
  activeMembers7d: number;
  activeMembers30d: number;
  totalPosts30d: number;
  totalComments30d: number;
  engagementRate: number;
  growthRate: number;
}

export interface CommunityAnalyticsTrendPoint {
  date: string;
  postCount: number;
  commentCount: number;
  newMemberCount: number;
}

export interface CommunityAnalyticsContributor {
  userId: string;
  userName: string;
  avatarUrl?: string | null;
  points: number;
  level: number;
  postCount: number;
  commentCount: number;
  lastActiveAt?: string | null;
}

export interface CommunityAnalyticsSnapshot {
  summary: CommunityAnalyticsSummary;
  daily: CommunityAnalyticsTrendPoint[];
  topContributors: CommunityAnalyticsContributor[];
}

function encodePathSegment(value: string): string {
  return encodeURIComponent(value);
}

function normalizeCheckoutUrl(rawUrl: string): string {
  const parsedUrl = new URL(rawUrl, window.location.origin);

  if (parsedUrl.protocol !== "https:" && parsedUrl.protocol !== "http:") {
    throw new Error("Invalid checkout URL returned by the community API.");
  }

  return parsedUrl.toString();
}

function mergePostsPage(
  previousPosts: CommunityPost[],
  nextPosts: CommunityPost[],
  page: number,
): CommunityPost[] {
  if (page <= 1) {
    return nextPosts;
  }

  const existingIds = new Set(previousPosts.map((post) => post.id));
  const appendedPosts = nextPosts.filter((post) => !existingIds.has(post.id));
  return [...previousPosts, ...appendedPosts];
}

export const __communityHookInternals = {
  mergePostsPage,
};

// ---------------------------------------------------------------------------
// C88: Spaces
// ---------------------------------------------------------------------------

export function useCommunitySpaces() {
  const [spaces, setSpaces] = useState<CommunitySpace[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchSpaces = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await apiFetch<{ data: CommunitySpace[] }>("/crm/communities/spaces");
      setSpaces(res.data ?? []);
    } catch { setSpaces([]); }
    finally { setIsLoading(false); }
  }, []);

  useEffect(() => { fetchSpaces(); }, [fetchSpaces]);
  return { spaces, isLoading, refetch: fetchSpaces };
}

export async function createSpace(data: {
  name: string;
  slug: string;
  description?: string;
  accessType?: string;
  coverImage?: string;
}): Promise<CommunitySpace> {
  const res = await apiFetch<{ data: CommunitySpace }>("/crm/communities/spaces", {
    method: "POST", body: JSON.stringify(data),
  });
  return res.data;
}

/** Join a space. For paid spaces, returns checkoutUrl for Stripe redirect. */
export async function joinSpace(spaceId: string): Promise<{ joined: boolean; checkoutUrl?: string }> {
  const res = await apiFetch<{ data: { joined: boolean; checkoutUrl?: string; alreadyMember?: boolean } }>(
    `/crm/communities/spaces/${encodePathSegment(spaceId)}/join`,
    { method: "POST" },
  );
  // Paid space — redirect to Stripe Checkout
  if (res.data.checkoutUrl) {
    const checkoutUrl = normalizeCheckoutUrl(res.data.checkoutUrl);
    window.location.assign(checkoutUrl);
    return { joined: false, checkoutUrl };
  }
  return { joined: res.data.joined };
}

/** Leave a space. For paid memberships, cancels Stripe subscription at period end. */
export async function leaveSpace(spaceId: string): Promise<{ left: boolean; cancelScheduled?: boolean; message?: string }> {
  const res = await apiFetch<{ data: { left: boolean; cancelScheduled?: boolean; message?: string } }>(
    `/crm/communities/spaces/${encodePathSegment(spaceId)}/leave`,
    { method: "POST" },
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// CM-T.1: Single space detail
// ---------------------------------------------------------------------------

export function useSpace(spaceId: string | undefined) {
  const [space, setSpace] = useState<CommunitySpace | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchSpace = useCallback(async () => {
    if (!spaceId) return;
    setIsLoading(true);
    try {
      const res = await apiFetch<{ data: CommunitySpace }>(`/crm/communities/spaces/${encodePathSegment(spaceId)}`);
      setSpace(res.data ?? null);
    } catch { setSpace(null); }
    finally { setIsLoading(false); }
  }, [spaceId]);

  useEffect(() => { fetchSpace(); }, [fetchSpace]);
  return { space, isLoading, refetch: fetchSpace };
}

// ---------------------------------------------------------------------------
// C89: Posts + comments + reactions
// ---------------------------------------------------------------------------

export function useCommunityFeed(spaceId: string | undefined, page = 1) {
  const [posts, setPosts] = useState<CommunityPost[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const fetchFeed = useCallback(async () => {
    if (!spaceId) return;
    setIsLoading(true);
    try {
      const qs = page > 1 ? `?page=${page}` : "";
      const res = await apiFetch<{ data: CommunityPost[]; hasMore: boolean }>(`/crm/communities/spaces/${encodePathSegment(spaceId)}/posts${qs}`);
      setPosts((currentPosts) => mergePostsPage(currentPosts, res.data ?? [], page));
      setHasMore(res.hasMore ?? false);
    } catch {
      setPosts((currentPosts) => (page > 1 ? currentPosts : []));
      setHasMore(false);
    }
    finally { setIsLoading(false); }
  }, [page, spaceId]);

  useEffect(() => { fetchFeed(); }, [fetchFeed]);
  return { posts, hasMore, isLoading, refetch: fetchFeed };
}

export async function createPost(spaceId: string, data: {
  postType: string;
  title?: string;
  content: string;
  mediaUrls?: string[];
  pollOptions?: Array<{ text: string }>;
}): Promise<CommunityPost> {
  const res = await apiFetch<{ data: CommunityPost }>(`/crm/communities/spaces/${encodePathSegment(spaceId)}/posts`, {
    method: "POST", body: JSON.stringify(data),
  });
  return res.data;
}

export async function updateSpaceSettings(
  spaceId: string,
  data: {
    aiManager: {
      enabled?: boolean;
      autoModerate?: boolean;
      autoReplyToQuestions?: boolean;
    };
  },
): Promise<CommunitySpace> {
  const res = await apiFetch<{ data: CommunitySpace }>(
    `/crm/communities/spaces/${encodePathSegment(spaceId)}/settings`,
    {
      method: "PATCH",
      body: JSON.stringify(data),
    },
  );
  return res.data;
}

export async function resetSpaceFromTemplate(spaceId: string): Promise<CommunitySpace> {
  const res = await apiFetch<{ data: CommunitySpace }>(
    `/crm/communities/spaces/${encodePathSegment(spaceId)}/reset-template`,
    {
      method: "POST",
    },
  );
  return res.data;
}

export async function toggleReaction(postId: string, reactionType: string): Promise<void> {
  await apiFetch(`/crm/communities/posts/${encodePathSegment(postId)}/react`, {
    method: "POST", body: JSON.stringify({ reactionType }),
  });
}

export async function votePoll(postId: string, optionId: string): Promise<void> {
  await apiFetch(`/crm/communities/posts/${encodePathSegment(postId)}/vote`, {
    method: "POST", body: JSON.stringify({ optionId }),
  });
}

export function usePostComments(postId: string | undefined) {
  const [comments, setComments] = useState<CommunityComment[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchComments = useCallback(async () => {
    if (!postId) return;
    setIsLoading(true);
    try {
      const res = await apiFetch<{ data: CommunityComment[] }>(`/crm/communities/posts/${encodePathSegment(postId)}/comments`);
      setComments(res.data ?? []);
    } catch { setComments([]); }
    finally { setIsLoading(false); }
  }, [postId]);

  useEffect(() => { fetchComments(); }, [fetchComments]);
  return { comments, isLoading, refetch: fetchComments };
}

export async function createComment(postId: string, data: { content: string; parentId?: string }): Promise<void> {
  await apiFetch(`/crm/communities/posts/${encodePathSegment(postId)}/comments`, {
    method: "POST", body: JSON.stringify(data),
  });
}

// ---------------------------------------------------------------------------
// C90: Gamification
// ---------------------------------------------------------------------------

export async function listCommunityBadges(
  spaceId: string,
  includeInactive = false,
): Promise<CommunityBadge[]> {
  const query = includeInactive ? "?includeInactive=true" : "";
  const res = await apiFetch<{ data: CommunityBadge[] }>(
    `/crm/communities/spaces/${encodePathSegment(spaceId)}/badges${query}`,
  );
  return res.data ?? [];
}

export function useSpaceBadges(spaceId: string | undefined, includeInactive = false) {
  const [badges, setBadges] = useState<CommunityBadge[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchBadges = useCallback(async () => {
    if (!spaceId) return;
    setIsLoading(true);
    try {
      setBadges(await listCommunityBadges(spaceId, includeInactive));
    } catch {
      setBadges([]);
    } finally {
      setIsLoading(false);
    }
  }, [includeInactive, spaceId]);

  useEffect(() => {
    fetchBadges();
  }, [fetchBadges]);

  return { badges, isLoading, refetch: fetchBadges };
}

export async function createCommunityBadge(
  spaceId: string,
  data: CreateCommunityBadgeInput,
): Promise<CommunityBadge> {
  const res = await apiFetch<{ data: CommunityBadge }>(
    `/crm/communities/spaces/${encodePathSegment(spaceId)}/badges`,
    { method: "POST", body: JSON.stringify(data) },
  );
  return res.data;
}

export async function awardCommunityBadge(
  spaceId: string,
  badgeId: string,
  userId: string,
): Promise<{ awarded: boolean; award: CommunityBadgeAward | null }> {
  const res = await apiFetch<{ data: { awarded: boolean; award: CommunityBadgeAward | null } }>(
    `/crm/communities/spaces/${encodePathSegment(spaceId)}/badges/${encodePathSegment(badgeId)}/award`,
    { method: "POST", body: JSON.stringify({ userId }) },
  );
  return res.data;
}

export async function revokeCommunityBadge(
  spaceId: string,
  badgeId: string,
  userId: string,
): Promise<{ revoked: boolean }> {
  const res = await apiFetch<{ data: { revoked: boolean } }>(
    `/crm/communities/spaces/${encodePathSegment(spaceId)}/badges/${encodePathSegment(badgeId)}/award/${encodePathSegment(userId)}`,
    { method: "DELETE" },
  );
  return res.data;
}

export function useLeaderboard(spaceId: string | undefined) {
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!spaceId) return;
    setIsLoading(true);
    apiFetch<{ data: LeaderboardEntry[] }>(`/crm/communities/spaces/${encodePathSegment(spaceId)}/leaderboard`)
      .then((res) => setEntries(res.data ?? []))
      .catch(() => setEntries([]))
      .finally(() => setIsLoading(false));
  }, [spaceId]);

  return { entries, isLoading };
}

export function useSpaceMembers(spaceId: string | undefined) {
  const [members, setMembers] = useState<CommunityMember[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!spaceId) return;
    setIsLoading(true);
    apiFetch<{ data: CommunityMember[] }>(`/crm/communities/spaces/${encodePathSegment(spaceId)}/members`)
      .then((res) => setMembers(res.data ?? []))
      .catch(() => setMembers([]))
      .finally(() => setIsLoading(false));
  }, [spaceId]);

  return { members, isLoading };
}

export function useCommunityMembers(params: {
  search?: string;
  role?: "member" | "moderator" | "admin" | "all";
  sort?: "activity" | "newest" | "alpha";
  limit?: number;
  offset?: number;
}) {
  const [members, setMembers] = useState<CommunityMember[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const fetchMembers = useCallback(async () => {
    const query = new URLSearchParams();
    if (params.search?.trim()) {
      query.set("search", params.search.trim());
    }
    if (params.role && params.role !== "all") {
      query.set("role", params.role);
    }
    query.set("sort", params.sort ?? "activity");
    query.set("limit", String(params.limit ?? 20));
    query.set("offset", String(params.offset ?? 0));

    setIsLoading(true);
    try {
      const res = await apiFetch<{ data: CommunityMember[]; hasMore: boolean }>(
        `/crm/communities/members?${query.toString()}`,
      );
      setMembers(res.data ?? []);
      setHasMore(res.hasMore ?? false);
    } catch {
      setMembers([]);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  }, [params.limit, params.offset, params.role, params.search, params.sort]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  return { members, hasMore, isLoading, refetch: fetchMembers };
}

export function useCommunityAnalytics() {
  const [analytics, setAnalytics] = useState<CommunityAnalyticsSnapshot | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchAnalytics = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await apiFetch<{ data: CommunityAnalyticsSnapshot }>("/crm/communities/analytics");
      setAnalytics(res.data ?? null);
    } catch {
      setAnalytics(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  return { analytics, isLoading, refetch: fetchAnalytics };
}
